import React from "react";

import styled from "styled-components";
import Slider from "@mui/material/Slider";

import { DataFormControl } from "../DataFormControl";
import { IDataDefinition } from "../../../rule-engine-core/Common/Data/Definitions/IDataDefinition";
import { PropertyEditorBoolean } from "../../../CommonUI/PropertyEditing/PropertyEditorBoolean";
import { PropertyEditorTextList } from "../../../CommonUI/PropertyEditing/PropertyEditorTextList";
import { StringDataDefinition } from "../../../rule-engine-core/Common/Data/Definitions/StringDataDefinition";
import { PropertyEditorInteger } from "../../../CommonUI/PropertyEditing/PropertyEditorInteger";
import { PropertyEditorText } from "../../../CommonUI/PropertyEditing/PropertyEditorText";
import { DataUtilities } from "../../../rule-engine-core/Common/DataUtilities";
import { UIStyleConstants } from "../../../rule-engine-core/Common/UIStyleConstants";
import { PropertyEditorCollapse } from "../../../CommonUI/PropertyEditing/PropertyEditorCollapse";
import { UISpacedBoxContent } from "../../../CommonUI/BuildlingBlocks/UISpacedBoxContent";
import { UILabel } from "../../../CommonUI/BuildlingBlocks/UILabel";

class XValueSliderEditUI extends React.Component<{ editMe: ValueSliderFormControl }> {
    render() {
        return (
            <UISpacedBoxContent>
                {this.props.editMe.renderBaseDataControlEditor()}

                <PropertyEditorBoolean
                    editObject={this.props.editMe}
                    label={"Select Value To Left"}
                    propertyName={"selectValuesToLeft"}
                />

                <PropertyEditorBoolean
                    editObject={this.props.editMe}
                    label={"Values from data"}
                    propertyName={"isDynamic"}
                />

                <PropertyEditorCollapse
                    editObject={this.props.editMe}
                    collapseLogicOrField={"isDynamic"}
                >
                    <PropertyEditorText
                        label={"Value Name For Steps"}
                        propertyName={"stepsValueName"}
                        editObject={this.props.editMe}
                    />
                </PropertyEditorCollapse>

                <PropertyEditorCollapse
                    editObject={this.props.editMe}
                    collapseLogicOrField={"isDynamic"}
                    showIfFalse={true}
                >
                    <PropertyEditorTextList
                        propertyName={"sliderValues"}
                        editObject={this.props.editMe}
                    />
                </PropertyEditorCollapse>

                <PropertyEditorInteger
                    label={"Label Size"}
                    propertyName={"labelSize"}
                    editObject={this.props.editMe}
                />
            </UISpacedBoxContent>
        );
    }
}

export class ValueSliderFormControl extends DataFormControl {
    readonly __type: string = "Value Slider";
    selectValuesToLeft: boolean = false;
    sliderValues: string[] = [];

    isDynamic: boolean = false;

    labelSize?: number;

    stepsValueName?: string;

    constructor() {
        super();
        this.canFocus = false;
    }

    render() {
        return (
            <>
                {this.label && <UILabel variant={"form_label"}>{this.label}</UILabel>}
                <div>
                    <StyledSlider
                        min={0}
                        max={this.sliderValues.length - 1}
                        defaultValue={this.getValue()}
                        step={1}
                        aria-labelledby="track-inverted-slider"
                        track={this.selectValuesToLeft ? "normal" : this.selectValuesToLeft}
                        getAriaValueText={this.valuetext}
                        onChange={this.handleChange}
                        data-testid={this.valueName}
                        value={this.getValue()}
                        labelsize={this.labelSize}
                        marks={this.getStepValues().map((v, index) => {
                            return {
                                label: v,
                                value: index,
                            };
                        })}
                    />
                </div>
            </>
        );
    }

    handleChange = (event: any, newValue: number | number[]) => {
        if (this.valueName) {
            let newIdx: number = 0;
            if (Array.isArray(newValue)) {
                newIdx = newValue[newValue.length - 1];
            } else {
                newIdx = newValue;
            }
            this.setFormDataValue(this.valueName, this.sliderValues[newIdx]);
        }
    };

    valuetext = (index: number) => {
        return this.getStepValues()[index];
    };

    getStepValues = () => {
        let steps = this.sliderValues;
        if (this.isDynamic && this.stepsValueName) {
            steps = this.getFormDataValue(this.stepsValueName) as string[];
        }
        return steps;
    };

    getValue(): number {
        let steps = this.getStepValues();

        if (this.valueName) {
            let value = this.getFormDataValue(this.valueName);
            return steps.indexOf(value);
        }
        return -1;
    }

    getAddedData(): IDataDefinition | IDataDefinition[] | undefined {
        if (!this.valueName) return;

        let def = new StringDataDefinition();
        def.list = true;
        def.name = this.valueName;
        return def;
    }

    renderEditUI(): JSX.Element | null {
        return <XValueSliderEditUI editMe={this} />;
    }

    getStorageData(): object {
        let retData = super.getStorageData();

        retData = DataUtilities.addStorageData(
            this,
            retData,
            "__type",
            "selectValuesToLeft",
            "sliderValues",
            "isDynamic",
            "labelSize",
            "stepsValueName",
        );

        return retData;
    }

    setStorageData(data: object): void {
        super.setStorageData(data);

        DataUtilities.setStorageData(
            this,
            data,
            "__type",
            "selectValuesToLeft",
            "sliderValues",
            "isDynamic",
            "labelSize",
            "stepsValueName",
        );
    }
}

const StyledSlider = styled(Slider)<{ labelsize?: number }>`
    .MuiSlider-thumb.MuiSlider-thumbColorPrimary {
        background: ${UIStyleConstants.WHITE_COLOR};
        border: 1px solid ${UIStyleConstants.GRAY_LIGHTER_COLOR};
        box-sizing: border-box;
        box-shadow: 0px 2px 6px -2px ${UIStyleConstants.GRAY_LIGHTER_COLOR};
        border-radius: 12px;
    }

    .MuiSlider-rail {
        height: 4px;
        background: ${UIStyleConstants.GRAY_LIGHT_COLOR};
    }

    .MuiSlider-track {
        height: 4px;
        background: ${UIStyleConstants.BLUE_COLOR};
    }

    .MuiSlider-mark {
        height: 4px;
        background: ${UIStyleConstants.GRAY_LIGHT_COLOR};
    }

    .MuiSlider-markLabel {
        font-size: ${(props) => props.labelsize || 9}px;
        font-style: normal;
        font-weight: 400;
        line-height: 1.5;
        letter-spacing: 0px;
        text-align: center;

        color: ${UIStyleConstants.TEXT_COLOR_DEFAULT};

        &.MuiSlider-markLabelActive {
            color: ${UIStyleConstants.BLUE_COLOR};
        }
    }
`;
